import React from 'react'
import { Link } from 'react-router-dom'
import { Home, Search, Grid3X3, User, Briefcase, CalendarCheck, PlusCircle, Inbox, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { useTranslation } from 'react-i18next'

/* ------------------------------------------------------------------ */
/*  MobileNav — fixed bottom tab bar for mobile                        */
/*  Role-aware tabs: guest/customer/provider                           */
/*  44×44 touch targets, safe-area inset padding                       */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

type UserRole = 'guest' | 'customer' | 'provider' | 'admin'

export interface MobileNavProps {
  /** Current pathname for active tab highlighting */
  currentPath?: string
  /** User role determines which tabs to show */
  role?: UserRole
}

interface TabItem {
  href: string
  labelKey: string
  icon: React.ElementType
}

const guestTabs: TabItem[] = [
  { href: '/', labelKey: 'nav.home', icon: Home },
  { href: '/tjanster', labelKey: 'nav.services', icon: Search },
  { href: '/hur-det-fungerar', labelKey: 'nav.howItWorks', icon: Grid3X3 },
  { href: '/logga-in', labelKey: 'nav.login', icon: User },
]

const customerTabs: TabItem[] = [
  { href: '/', labelKey: 'nav.home', icon: Home },
  { href: '/dashboard/bokningar', labelKey: 'nav.bookings', icon: CalendarCheck },
  { href: '/dashboard/ny-fraga', labelKey: 'nav.newRequest', icon: PlusCircle },
  { href: '/dashboard/installningar', labelKey: 'nav.account', icon: User },
]

const providerTabs: TabItem[] = [
  { href: '/leverantor-portal', labelKey: 'nav.overview', icon: Briefcase },
  { href: '/leverantor-portal/inkorg', labelKey: 'nav.inbox', icon: Inbox },
  { href: '/leverantor-portal/kalender', labelKey: 'nav.calendar', icon: Calendar },
  { href: '/leverantor-portal/installningar', labelKey: 'nav.account', icon: User },
]

function getTabsForRole(role: UserRole): TabItem[] {
  switch (role) {
    case 'customer':
      return customerTabs
    case 'provider':
    case 'admin':
      return providerTabs
    default:
      return guestTabs
  }
}

export function MobileNav({ currentPath = '/', role = 'guest' }: MobileNavProps) {
  const { t } = useTranslation()
  const tabs = getTabsForRole(role)

  return (
    <nav
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-neutral-0 md:hidden',
        // Safe-area inset for iOS notch / home indicator
        'pb-[env(safe-area-inset-bottom,0px)]',
      )}
      aria-label="Mobilnavigation"
    >
      <div className="flex items-stretch justify-around">
        {tabs.map((tab) => {
          const isActive =
            tab.href === '/'
              ? currentPath === '/'
              : currentPath === tab.href || currentPath?.startsWith(tab.href + '/')

          return (
            <Link
              key={tab.href}
              to={tab.href}
              className={cn(
                // 44×44 minimum touch target
                'flex min-h-[44px] min-w-[44px] flex-1 flex-col items-center justify-center gap-0.5 py-2',
                'text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                isActive ? 'text-primary-600' : 'text-neutral-500 active:text-neutral-700',
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              <tab.icon className="h-5 w-5" aria-hidden="true" />
              <span className="truncate">{t(tab.labelKey)}</span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

MobileNav.displayName = 'MobileNav'
